import { IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";
import React from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import { CSVLink } from "react-csv";

import { getDateString } from "../../../libs/Util";
import { IBoxesDashboardParams } from "../../../constants/boxDashboard";
import {
  Context,
  FETCH_BOXES_DASHBOARD_SUCCESS_MESSAGE,
  FETCH_TEACHERS_SUCCESS,
  FETCH_TEACHERS_FAILURE,
  clearSuccessMessage,
  fetchMyBoxSummary,
  bulkCheckRealizations,
  gearboxHidden,
  fetchTeacherGearboxDirectories,
  fetchRealizationsCSVDataByGearbox,
  fetchClassesIncludePast,
  fetchTeachers,
  fetchBoxesDashboard,
} from "../../../store/teacher";
import { EMPTY_CLASS_MESSAGE } from "../../../constants/message";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import NavToolbar from "../../../organisms/teacher/NavToolbar";
import BoxDashboard from "../../../templates/teacher/BoxDashboard";
import styles from "../common.module.scss";
import { REALIZATIONS_CSV_HEADERS } from "../../../constants/analyzeConstant";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import { convertRealizationsToCsv } from "../../../libs/convert_realizations_to_csv";

const BoxDashboardPage = () => {
  const { dispatch, contextState } = React.useContext(Context);
  const history = useHistory();
  const [calling, peep] = useWaitApiCall(contextState);
  const [teachersFetched, setTeachersFetched] = React.useState(
    contextState.teachers.length > 0,
  );
  const queryString = React.useMemo(
    () => history.location.search,
    [history.location.search],
  );
  const hasNoClasses =
    contextState.teacher.id !== 0 && contextState.teacher.classes.length === 0;

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);

  useIonViewWillEnter(() => {
    dispatch(fetchClassesIncludePast());
    dispatch(fetchTeachers());
    dispatch(fetchMyBoxSummary());
  }, [queryString, dispatch]);

  React.useEffect(() => {
    if (
      contextState.lastAction === FETCH_TEACHERS_SUCCESS ||
      contextState.lastAction === FETCH_TEACHERS_FAILURE
    ) {
      setTeachersFetched(true);
    }
  }, [contextState.lastAction]);

  React.useEffect(() => {
    if (
      contextState.success_message === FETCH_BOXES_DASHBOARD_SUCCESS_MESSAGE
    ) {
      dispatch(clearSuccessMessage());
      dispatch(fetchTeacherGearboxDirectories());
    }
  }, [dispatch, contextState.success_message]);

  const [fetchBoxesDashboardFunc] = useDebounceCallback(
    (params: IBoxesDashboardParams) => {
      dispatch(peep(fetchBoxesDashboard(params)));
    },
    500,
  );

  const handleBulkCheckRealizations = React.useCallback(
    (gear_box_id: number) => {
      dispatch(peep(bulkCheckRealizations(gear_box_id)));
    },
    [dispatch, peep],
  );

  const gearboxHiddenFunc = React.useCallback(
    (id: number) => {
      dispatch(peep(gearboxHidden(id)));
    },
    [dispatch, peep],
  );

  const getRealizationsCSVDataFunc = React.useCallback(
    (id: number) => {
      dispatch(peep(fetchRealizationsCSVDataByGearbox(id)));
    },
    [dispatch, peep],
  );

  const csvData = React.useMemo(() => {
    if (!contextState.realizations_csv_data_by_gearbox) {
      return [];
    }
    const { realizations } = contextState.realizations_csv_data_by_gearbox;
    return convertRealizationsToCsv(realizations);
  }, [contextState.realizations_csv_data_by_gearbox]);

  useDidMountEffect(() => {
    const timeout = setTimeout(() => {
      csvData.length &&
        csvLinkRef.current?.link &&
        csvLinkRef.current.props.data.length &&
        csvLinkRef.current.link.click();
    });
    return () => clearTimeout(timeout);
  }, [csvData]);

  return (
    <IonPage>
      <CSVLink
        ref={csvLinkRef}
        filename={`${getDateString()}_${
          contextState.realizations_csv_data_by_gearbox?.realizations[0]
            ?.gearbox_title
        }.csv`}
        headers={REALIZATIONS_CSV_HEADERS}
        data={csvData}
        uFEFF
        hidden
      />
      <IonContent className={`${styles.wrapper} ${styles.bgWhileBase}`}>
        <NavToolbar defaultSegment="submitted_stock" />
        {contextState.teacher.classes.length > 0 && (
          <div className={clsx(styles.container, styles.transparent)}>
            <BoxDashboard
              calling={calling}
              classes={contextState.classes}
              teachersFetched={teachersFetched}
              boxList={contextState.boxesDashboard}
              myBox={contextState.my_box}
              fetchBoxDashboard={fetchBoxesDashboardFunc}
              bulkCheckRealizations={handleBulkCheckRealizations}
              gearboxHiddenFunc={gearboxHiddenFunc}
              getRealizationsCSVDataFunc={getRealizationsCSVDataFunc}
              teacherId={contextState.teacher.id}
              assignClassIds={contextState.teacher.classes.map(cls => cls.id)}
              teacherGearboxDirectories={contextState.teacherGearboxDirectories}
            />
          </div>
        )}
        {hasNoClasses && (
          <div className={styles.container}>
            <p className={clsx(styles.positionMiddle, styles.message)}>
              {EMPTY_CLASS_MESSAGE}
            </p>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default BoxDashboardPage;
